<template>
  <aside class="admin-sidebar" :class="{ 'is-open': isOpen }">
    <nav class="admin-nav">
      <ul>
        <li v-for="item in visibleNavItems" :key="item.to">
          <router-link :to="item.to" active-class="active-link" @click="$emit('close')">
            <i :class="item.icon"></i> {{ item.label }}
          </router-link>
        </li>
      </ul>
    </nav>
    <div class="sidebar-footer">
      <button @click="logoutUser" class="btn btn-danger btn-block">
        <i class="fas fa-sign-out-alt"></i> Logout
      </button>
    </div>
  </aside>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/store/auth.store';
import { OWNER_LEVEL_ROLES, ADMIN_PORTAL_ROLES } from '@/constants/roles';

interface NavItem {
  to: string;
  icon: string;
  label: string;
  roles: string[];
}

export default defineComponent({
  name: 'AdminSidebar',
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close'],
  setup() {
    const router = useRouter();
    const authStore = useAuthStore();

    const allNavItems = ref<NavItem[]>([
      { to: '/admin/appointments', icon: 'fas fa-calendar-check', label: 'Appointments', roles: [...ADMIN_PORTAL_ROLES] },
      { to: '/admin/customers', icon: 'fas fa-users', label: 'Customers', roles: [...ADMIN_PORTAL_ROLES] },
      { to: '/admin/services', icon: 'fas fa-cut', label: 'Services', roles: [...OWNER_LEVEL_ROLES] },
      { to: '/admin/employees', icon: 'fas fa-user-tie', label: 'Employees', roles: [...OWNER_LEVEL_ROLES] },
      { to: '/admin/offers', icon: 'fas fa-tags', label: 'Offers', roles: [...OWNER_LEVEL_ROLES] },
      { to: '/admin/spin-campaigns', icon: 'fas fa-random', label: 'Spin Campaigns', roles: [...OWNER_LEVEL_ROLES] },
      { to: '/admin/gallery', icon: 'fas fa-images', label: 'Gallery', roles: [...ADMIN_PORTAL_ROLES] },
      { to: '/admin/reports', icon: 'fas fa-chart-line', label: 'Reports', roles: [...OWNER_LEVEL_ROLES] },
    ]);

    const userRole = computed(() => authStore.user?.role?.name);

    const visibleNavItems = computed(() => {
      const role = userRole.value?.toLowerCase();
      if (!role) {
        return [];
      }
      return allNavItems.value.filter(item => item.roles.includes(role));
    });

    const logoutUser = () => {
      authStore.logout();
      router.push('/login');
    };

    return {
      logoutUser,
      visibleNavItems,
    };
  },
});
</script>

<style scoped>
.admin-sidebar {
  width: var(--sidebar-width);
  background-color: var(--brand-black, #1a1a1a);
  color: var(--text-light, #fff);
  display: flex;
  flex-direction: column;
  position: fixed;
  left: 0;
  top: var(--header-height);
  bottom: 0;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
  z-index: 1002;
  transition: transform 0.3s ease;
}

.admin-nav {
  flex-grow: 1;
  overflow-y: auto;
  padding-top: 20px;
  -webkit-overflow-scrolling: touch;
}

.admin-nav ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.admin-nav li a {
  display: flex;
  align-items: center;
  padding: 14px 20px;
  color: #ccc;
  text-decoration: none;
  transition: background-color 0.2s ease, color 0.2s ease;
  font-size: 0.95em;
  min-height: var(--touch-min);
  box-sizing: border-box;
}

.admin-nav li a i {
  margin-right: 12px;
  width: 20px;
  text-align: center;
}

.admin-nav li a:hover {
  background-color: rgba(255, 255, 255, 0.05);
  color: var(--text-light, #fff);
}

.admin-nav li a.active-link {
  background-color: var(--brand-gold, #DAA520);
  color: var(--brand-black, #1a1a1a);
  font-weight: bold;
  border-left: 3px solid var(--brand-black);
  padding-left: 17px;
}

.admin-nav li a.active-link i {
  color: var(--brand-black, #1a1a1a);
}

.sidebar-footer {
  padding: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
}

.btn-block {
  width: 100%;
  text-align: center;
  min-height: var(--touch-min);
}

.btn-danger {
  background-color: #dc3545;
  border: none;
  color: white;
  cursor: pointer;
  border-radius: 4px;
  padding: 10px 16px;
}

.btn-danger:hover {
  background-color: #c82333;
}

@media (max-width: 992px) {
  .admin-sidebar {
    transform: translateX(-100%);
  }

  .admin-sidebar.is-open {
    transform: translateX(0);
  }
}
</style>
